import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Cpu, RefreshCw, Zap, Wrench, RotateCcw, CheckCircle, XCircle, Radio, Activity, Gauge, Layers3, AlertTriangle, FileText, Search } from 'lucide-react';
import { useSystemHealth, useSubscriptions, useRuntimeMonitoring, useAllRuntimeLogs } from '../../api/hooks';
import { useSessionStore } from '../../session/store';
import { systemApi } from '../../api/client';
import { SectionHeader, StatCard, ConfirmModal, Spinner, DataTable, ErrorState, Tabs } from '../../components/ui';
import { fmtDateTime, fmtRelative, cn } from '../../lib/utils';
import { toast } from '../../store';

function HealthRow({ label, ok, detail, endpoint }) {
  return (
    <div className={cn(
      'flex items-center justify-between p-3.5 rounded-lg border',
      ok === true  ? 'bg-bull/5 border-bull/20' :
      ok === false ? 'bg-bear/5 border-bear/20' :
                     'bg-bg-surface border-bg-border'
    )}>
      <div className="flex items-center gap-3">
        {ok === true  ? <CheckCircle size={14} className="text-bull" />
                      : ok === false ? <XCircle size={14} className="text-bear" />
                      : <div className="w-4 h-4 rounded-full border border-txt-muted" />}
        <div>
          <div className="text-sm font-semibold text-txt-primary">{label}</div>
          {detail   && <div className="text-2xs font-mono text-txt-muted">{detail}</div>}
          {endpoint && <div className="text-2xs font-mono text-txt-muted mt-0.5">{endpoint}</div>}
        </div>
      </div>
      <span className={cn('badge border',
        ok === true  ? 'bg-bull/10 border-bull/30 text-bull' :
        ok === false ? 'bg-bear/10 border-bear/30 text-bear' :
                       'bg-bg-hover border-bg-border text-txt-muted'
      )}>
        {ok === true ? 'UP' : ok === false ? 'DOWN' : '?'}
      </span>
    </div>
  );
}

function MetricLine({ label, value, tone }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/40 last:border-b-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className={cn('text-xs font-mono', tone || 'text-txt-secondary')}>{value ?? '—'}</span>
    </div>
  );
}

function SectionLabel({ icon: Icon, title, extra }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={13} className="text-txt-label" />
      <span className="text-2xs font-mono text-txt-label uppercase tracking-widest">{title}</span>
      {extra && <span className="ml-auto text-2xs font-mono text-txt-muted">{extra}</span>}
    </div>
  );
}

function formatMetric(value, suffix = '') {
  if (value == null) return '—';
  return `${value}${suffix}`;
}

function formatMs(value) {
  if (value == null) return '—';
  return `${Number(value).toFixed(1)} ms`;
}

function formatBool(value) {
  if (value == null) return '—';
  return value ? 'YES' : 'NO';
}

const CONTROLS = [
  {
    key: 'warmup',
    title: 'Warmup',
    endpoint: 'POST /warmup',
    desc: 'Pre-load candle history and initialize strategy runtime for all active symbols',
    icon: Zap,
    confirm: 'Pre-load candle history for all active symbols. Existing runtimes reinitialize.',
    fn: () => systemApi.warmup({}),
  },
  {
    key: 'repair',
    title: 'Repair Symbol',
    endpoint: 'POST /repair-symbol',
    desc: 'Attempt repair of broken WebSocket subscriptions and Redis pub/sub channels',
    icon: Wrench,
    confirm: 'Attempt to repair broken connections. Active strategies may pause briefly.',
    fn: () => systemApi.repairSymbol({}),
  },
  {
    key: 'recover',
    title: 'Recover Symbol',
    endpoint: 'POST /recover-symbol',
    desc: 'Full recovery: restart subsystems and reload all state from database',
    icon: RotateCcw,
    confirm: 'Full system recovery. All running strategies will suspend temporarily. This is a heavy operation.',
    fn: () => systemApi.recoverSymbol({}),
  },
];

const RUNTIME_TABS = [
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'logs', label: 'Runtime Logs' },
];

const LOG_LEVELS = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'];

const LOG_LEVEL_STYLE = {
  ERROR: 'text-bear',
  WARN: 'text-warn',
  INFO: 'text-txt-secondary',
  DEBUG: 'text-txt-muted',
};

function useRuntimeControlMutation(control, refetch) {
  return useMutation({
    mutationFn: control.fn,
    onSuccess: () => { toast.success(`${control.title} triggered`); refetch(); },
    onError: (e) => toast.error(e.message),
  });
}

function getTodayIstDate() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date());
}

function AdminRuntimeLogsTab() {
  const todayIst = getTodayIstDate();
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [fromDate, setFromDate] = useState(todayIst);
  const [toDate, setToDate] = useState(todayIst);
  const [appliedRange, setAppliedRange] = useState({ from: todayIst, to: todayIst });

  const queryParams = {
    limit: 1000,
    offset: 0,
    from_date: appliedRange.from || todayIst,
    to_date: appliedRange.to || todayIst,
    ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
  };

  const { data = [], isLoading, error, refetch } = useAllRuntimeLogs(queryParams);
  const sorted = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const filtered = levelFilter === 'ALL'
    ? sorted
    : sorted.filter((row) => row.level?.toUpperCase() === levelFilter);

  const applyDates = () => {
    const start = fromDate || todayIst;
    const end = toDate || todayIst;
    if (start > end) return;
    setAppliedRange({ from: start, to: end });
  };

  const resetDates = () => {
    setFromDate(todayIst);
    setToDate(todayIst);
    setAppliedRange({ from: todayIst, to: todayIst });
  };

  const columns = [
    { key: 'created_at', label: 'Time', render: (value) => fmtDateTime(value), width: '150px' },
    {
      key: 'level',
      label: 'Level',
      width: '80px',
      render: (value) => (
        <span className={cn('uppercase font-bold', LOG_LEVEL_STYLE[value?.toUpperCase()] || 'text-txt-muted')}>
          {value || '—'}
        </span>
      ),
    },
    { key: 'deployment_id', label: 'Dep', render: (value) => `#${value ?? '?'}`, width: '80px' },
    {
      key: 'owner_user_id',
      label: 'User',
      render: (value, row) => {
        const email = row.owner_email || row.owner_username;
        return email ? `${email}${value ? ` (#${value})` : ''}` : (value ? `#${value}` : '—');
      },
      width: '260px',
    },
    { key: 'message', label: 'Message', render: (value) => <span className="break-all">{value || '—'}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="panel p-4 flex items-center gap-3 flex-wrap">
        <FileText size={13} className="text-amber shrink-0" />
        <input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && setAppliedSearch(searchText)}
          placeholder="Search user id, email, deployment id..."
          className="input-base w-72 text-xs"
        />
        <button onClick={() => setAppliedSearch(searchText)} className="btn-primary text-xs flex items-center gap-1.5 py-1.5">
          <Search size={11} /> Search
        </button>
        {appliedSearch && (
          <button
            onClick={() => { setSearchText(''); setAppliedSearch(''); }}
            className="btn-ghost text-xs py-1.5"
          >
            Clear
          </button>
        )}
        <span className="text-xs text-txt-muted font-mono ml-2">From:</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="input-base w-40 text-xs" />
        <span className="text-xs text-txt-muted font-mono">To:</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="input-base w-40 text-xs" />
        <button onClick={applyDates} disabled={!fromDate || !toDate || fromDate > toDate} className="btn-primary text-xs py-1.5">
          Apply
        </button>
        <button onClick={resetDates} className="btn-ghost text-xs py-1.5">
          Reset
        </button>
        <button onClick={refetch} className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 ml-auto">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {LOG_LEVELS.map((level) => (
          <button
            key={level}
            onClick={() => setLevelFilter(level)}
            className={cn(
              'px-2 py-1 rounded text-2xs font-mono transition-colors border',
              levelFilter === level
                ? 'bg-amber/10 border-amber/30 text-amber'
                : 'border-transparent text-txt-muted hover:text-txt-secondary',
            )}
          >
            {level}
          </button>
        ))}
        <span className="text-2xs font-mono text-txt-muted ml-auto">
          {filtered.length} / {sorted.length} lines • Date: {appliedRange.from} to {appliedRange.to} (IST)
        </span>
      </div>

      <div className="panel p-4">
        {error ? (
          <ErrorState message={error.message || 'Failed to load runtime logs'} onRetry={refetch} />
        ) : (
          <DataTable columns={columns} data={filtered} loading={isLoading} emptyText="No runtime logs found" />
        )}
      </div>
    </div>
  );
}

export default function AdminRuntimePage() {
  const sessionRole = useSessionStore((s) => s.role);
  const isOperator = sessionRole === 'operator';

  const { data: health, isLoading, refetch } = useSystemHealth();
  const { data: subs }  = useSubscriptions();
  const { data: monitoring, isLoading: monitoringLoading, error: monitoringError, refetch: refetchMonitoring } = useRuntimeMonitoring();
  const [confirm, setConfirm] = useState(null);
  const [tab, setTab] = useState(isOperator ? 'logs' : 'infrastructure');

  const [warmupControl, repairControl, recoverControl] = CONTROLS;
  const mutations = {
    warmup: useRuntimeControlMutation(warmupControl, refetch),
    repair: useRuntimeControlMutation(repairControl, refetch),
    recover: useRuntimeControlMutation(recoverControl, refetch),
  };

  const symbols = Array.isArray(subs?.subscriptions)
    ? subs.subscriptions
        .filter((item) => item?.live_effective !== false)
        .map((item) => item?.symbol)
        .filter(Boolean)
    : (subs?.symbols || (Array.isArray(subs) ? subs : []));
  const engine = monitoring?.engine_summary;
  const latency = monitoring?.latency;
  const shardRows = monitoring?.shards || [];
  const subscriptionDetail = monitoring?.subscriptions;
  const reconnect = monitoring?.reconnect_repair;
  const candlePipeline = monitoring?.candle_pipeline;
  const recentEvents = monitoring?.recent_events || [];
  const network = monitoring?.network;

  const healthItems = [
    { label: 'FYERS WebSocket v3',  ok: health?.websocket,    detail: 'Live market data feed',           endpoint: 'GET /health' },
    { label: 'Redis Pub/Sub',        ok: health?.redis,         detail: 'Candle-close event bus',          endpoint: 'GET /redis/health' },
    { label: 'Candle Feed Pipeline', ok: health?.candle_feed ?? health?.websocket, detail: 'OHLCV ingestion' },
    { label: 'Strategy API',         ok: health !== undefined,  detail: 'REST endpoints responsive' },
  ];

  const shardColumns = [
    { key: 'shard_id', label: 'Shard' },
    { key: 'queue_depth', label: 'Queue' },
    { key: 'fast_lane_depth', label: 'Fast Lane' },
    { key: 'events_processed', label: 'Processed' },
    { key: 'avg_latency_ms', label: 'Avg Latency', render: (value) => formatMs(value) },
    { key: 'max_latency_ms', label: 'Max Latency', render: (value) => formatMs(value) },
    { key: 'dropped_events', label: 'Dropped' },
    {
      key: 'degraded',
      label: 'Degraded',
      render: (value, row) => (
        <span className={cn(
          'badge border',
          value ? 'bg-bear/10 border-bear/30 text-bear' : 'bg-bull/10 border-bull/30 text-bull'
        )}>
          {value ? 'YES' : (row.task_alive ? 'NO' : 'IDLE')}
        </span>
      ),
    },
  ];

  const eventColumns = [
    { key: 'timestamp', label: 'Time', render: (value) => fmtDateTime(value) },
    { key: 'level', label: 'Level' },
    { key: 'code', label: 'Event' },
    { key: 'summary', label: 'Summary' },
  ];

  return (
    <div className="max-w-5xl space-y-6">
      <SectionHeader
        title={isOperator ? 'Runtime Logs' : 'Admin — Runtime'}
        subtitle={isOperator ? 'Logs for strategies under your users' : 'Worker pool, infrastructure health, manual controls'}
        actions={isOperator ? undefined :
          <button onClick={() => { refetch(); refetchMonitoring(); }} className="btn-ghost flex items-center gap-1.5 text-xs">
            <RefreshCw size={12} className={isLoading || monitoringLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />

      {!isOperator && <Tabs tabs={RUNTIME_TABS} active={tab} onChange={setTab} />}

      {tab === 'infrastructure' && (
        <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="WebSocket"    value={health?.websocket ? 'UP' : 'DOWN'}   accent={health?.websocket ? 'text-bull' : 'text-bear'} icon={Radio} />
        <StatCard label="Redis"        value={health?.redis      ? 'UP' : 'DOWN'}   accent={health?.redis      ? 'text-bull' : 'text-bear'} icon={Cpu} />
        <StatCard label="Subscriptions" value={symbols.length}   accent={symbols.length > 0 ? 'text-bull' : undefined} icon={Radio} />
        <StatCard label="Last Check"   value={health?.last_checked ? fmtDateTime(health.last_checked).split(' ')[1] : '—'} />
      </div>

      {/* Health grid */}
      <div className="panel p-4 space-y-2">
        <div className="flex items-center gap-2 mb-3">
          <Cpu size={13} className="text-txt-label" />
          <span className="text-2xs font-mono text-txt-label uppercase tracking-widest">Infrastructure Status</span>
          {health?.last_checked && (
            <span className="ml-auto text-2xs font-mono text-txt-muted">
              Last: {fmtDateTime(health.last_checked)}
            </span>
          )}
        </div>
        {healthItems.map((item) => <HealthRow key={item.label} {...item} />)}
      </div>

      {/* Active subscriptions */}
      {symbols.length > 0 && (
        <div className="panel p-4">
          <div className="flex items-center gap-2 mb-3">
            <Radio size={13} className="text-txt-label" />
            <span className="text-2xs font-mono text-txt-label uppercase tracking-widest">
              Active Subscriptions
            </span>
            <span className="badge bg-bull/10 border border-bull/30 text-bull ml-1">{symbols.length}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {symbols.map((s) => (
              <span key={s} className="badge bg-bg-surface border border-bg-border text-txt-secondary font-mono">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Network Outage Monitor */}
      <div className="panel p-4">
        <SectionLabel
          icon={Radio}
          title="Network Outage Monitor"
          extra={network?.last_checked_at ? `Checked ${fmtRelative(network.last_checked_at)}` : undefined}
        />
        <div className="flex items-center gap-2 mb-3">
          <span className={cn(
            'badge border',
            network?.status === 'ONLINE' ? 'bg-bull/10 border-bull/30 text-bull' :
            network?.status === 'OUTAGE' ? 'bg-bear/10 border-bear/30 text-bear' :
                                           'bg-bg-hover border-bg-border text-txt-muted'
          )}>
            {network?.status || 'UNKNOWN'}
          </span>
        </div>
        <MetricLine label="Last Checked At" value={network?.last_checked_at ? fmtDateTime(network.last_checked_at) : '—'} />
        <MetricLine
          label="Outage Started At"
          value={network?.current_outage_started_at ? fmtDateTime(network.current_outage_started_at) : '—'}
          tone={network?.current_outage_started_at ? 'text-bear' : undefined}
        />
        <MetricLine label="Last Recovered At" value={network?.last_recovered_at ? fmtDateTime(network.last_recovered_at) : '—'} />
        <div className="mt-3">
          <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Recent Events</span>
          {(network?.recent_events?.length ?? 0) === 0 ? (
            <div className="mt-2 text-xs text-txt-muted font-mono">No outage events recorded.</div>
          ) : (
            <div className="mt-2 space-y-1.5">
              {[...network.recent_events].slice(-5).reverse().map((evt, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-bg-border/40 last:border-b-0 text-xs font-mono">
                  <span className={cn(
                    'badge border shrink-0',
                    evt.type === 'OUTAGE_STARTED' ? 'bg-bear/10 border-bear/30 text-bear' : 'bg-bull/10 border-bull/30 text-bull'
                  )}>
                    {evt.type === 'OUTAGE_STARTED' ? 'STARTED' : 'ENDED'}
                  </span>
                  <span className="text-txt-secondary">
                    {evt.type === 'OUTAGE_STARTED'
                      ? fmtDateTime(evt.timestamp)
                      : `${fmtDateTime(evt.started_at)} → ${fmtDateTime(evt.ended_at)}${evt.duration_seconds != null ? ` · ${Number(evt.duration_seconds).toFixed(0)}s` : ''}`
                    }
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {monitoringError ? (
        <div className="panel p-4">
          <SectionLabel icon={AlertTriangle} title="Runtime Monitoring" extra="New sections unavailable" />
          <ErrorState message={monitoringError.message || 'Failed to load runtime monitoring'} onRetry={refetchMonitoring} />
        </div>
      ) : (
        <>
          <div>
            <SectionLabel
              icon={Activity}
              title="Strategy Engine Summary"
              extra={monitoringLoading ? 'Refreshing…' : (engine?.status ? `Status: ${engine.status}` : 'Awaiting data')}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
              <StatCard label="Strategy Engine" value={engine?.status || '—'} accent={engine?.status === 'DEGRADED' ? 'text-warn' : engine?.status === 'UP' ? 'text-bull' : 'text-bear'} icon={Activity} />
              <StatCard label="Worker Shards" value={formatMetric(engine?.worker_shards?.total)} sub={`Active ${formatMetric(engine?.worker_shards?.active)}`} icon={Layers3} />
              <StatCard label="Fast Lane" value={formatMetric(engine?.fast_lane?.current_depth)} sub={`Max ${formatMetric(engine?.fast_lane?.max_size)} · Dropped ${formatMetric(engine?.fast_lane?.dropped_fast_path_count)}`} icon={Zap} />
              <StatCard label="Normal Queue" value={formatMetric(engine?.normal_queue?.total_queued_events)} sub={`Max ${formatMetric(engine?.normal_queue?.max_queue_size)} · Warn ${formatMetric(engine?.normal_queue?.warn_threshold)}`} icon={Gauge} />
              <StatCard label="Degraded Mode" value={engine?.degraded_mode?.current_state ? 'ON' : 'OFF'} sub={engine?.degraded_mode?.reason || 'No active degraded reason'} accent={engine?.degraded_mode?.current_state ? 'text-warn' : 'text-bull'} icon={AlertTriangle} />
            </div>
          </div>

          <div>
            <SectionLabel icon={Gauge} title="Latency Metrics" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard label="Fast Path" value={formatMs(latency?.fast_path?.current_ms)} sub={`Avg ${formatMs(latency?.fast_path?.avg_ms)} · Max ${formatMs(latency?.fast_path?.max_ms)} · Warn ${formatMs(latency?.fast_path?.warn_threshold_ms)}`} icon={Zap} />
              <StatCard label="Normal Queue" value={formatMs(latency?.normal_queue?.current_ms)} sub={`Avg ${formatMs(latency?.normal_queue?.avg_ms)} · Max ${formatMs(latency?.normal_queue?.max_ms)}`} icon={Gauge} />
              <StatCard label="End To End" value={formatMs(latency?.end_to_end?.current_ms)} sub={`Avg ${formatMs(latency?.end_to_end?.avg_ms)} · Max ${formatMs(latency?.end_to_end?.max_ms)} · Error ${formatMs(latency?.end_to_end?.error_threshold_ms)}`} icon={Activity} />
            </div>
          </div>

          <div className="panel p-4">
            <SectionLabel icon={Layers3} title="Shard / Queue Detail" extra={`${shardRows.length} shard${shardRows.length === 1 ? '' : 's'}`} />
            <DataTable columns={shardColumns} data={shardRows} loading={monitoringLoading} emptyText="No shard metrics available" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="panel p-4">
              <SectionLabel icon={Radio} title="WebSocket / Subscription Detail" />
              <MetricLine label="Desired Raw Symbols" value={formatMetric(subscriptionDetail?.desired_raw_symbols_count)} />
              <MetricLine label="Desired Effective Symbols" value={formatMetric(subscriptionDetail?.desired_effective_symbols_count)} />
              <MetricLine label="Applied Symbols" value={formatMetric(subscriptionDetail?.applied_symbols_count)} />
              <MetricLine label="NSE_BSE Live" value={formatBool(subscriptionDetail?.market_group_live_enabled?.NSE_BSE)} tone={subscriptionDetail?.market_group_live_enabled?.NSE_BSE ? 'text-bull' : 'text-txt-secondary'} />
              <MetricLine label="MCX Live" value={formatBool(subscriptionDetail?.market_group_live_enabled?.MCX)} tone={subscriptionDetail?.market_group_live_enabled?.MCX ? 'text-bull' : 'text-txt-secondary'} />
              <MetricLine label="Reconnect Count" value={formatMetric(subscriptionDetail?.reconnect_count)} />
              <MetricLine label="Last Reconnect" value={subscriptionDetail?.last_reconnect_time ? `${fmtDateTime(subscriptionDetail.last_reconnect_time)} (${fmtRelative(subscriptionDetail.last_reconnect_time)})` : '—'} />
              <MetricLine label="Repair State" value={reconnect?.running ? 'RUNNING' : reconnect?.pending ? 'PENDING' : 'IDLE'} tone={reconnect?.running ? 'text-warn' : 'text-txt-secondary'} />
              <MetricLine label="Repair Duration" value={formatMs(reconnect?.last_run_duration_ms)} />
              <MetricLine label="Repair Attempted / Repaired / Failed" value={`${formatMetric(reconnect?.last_run_stats?.attempted)} / ${formatMetric(reconnect?.last_run_stats?.repaired)} / ${formatMetric(reconnect?.last_run_stats?.failed)}`} />
            </div>

            <div className="panel p-4">
              <SectionLabel icon={Cpu} title="Candle Pipeline Health" />
              <MetricLine label="Active Running Candles" value={formatMetric(candlePipeline?.active_running_candles)} />
              <MetricLine label="Active Symbols" value={formatMetric(candlePipeline?.active_symbols_count)} />
              <MetricLine label="Flush Loop Alive" value={formatBool(candlePipeline?.flush_loop_alive)} tone={candlePipeline?.flush_loop_alive ? 'text-bull' : 'text-bear'} />
              <MetricLine label="Last Persisted Candle" value={candlePipeline?.last_persisted_candle_time ? `${fmtDateTime(candlePipeline.last_persisted_candle_time)} (${fmtRelative(candlePipeline.last_persisted_candle_time)})` : '—'} />
              <MetricLine label="Persist Rate / Min" value={formatMetric(candlePipeline?.candle_persist_rate_per_min)} />
              <MetricLine label="HTF Dispatch Count" value={formatMetric(candlePipeline?.htf_aggregation?.dispatch_count)} />
              <MetricLine label="HTF Last Dispatch" value={candlePipeline?.htf_aggregation?.last_dispatch_time ? fmtDateTime(candlePipeline.htf_aggregation.last_dispatch_time) : '—'} />
              <MetricLine label="Out-Of-Order Drops" value={formatMetric(candlePipeline?.out_of_order_tick_drop_count)} />
              <MetricLine label="Post-Close Tick Drops" value={formatMetric(candlePipeline?.post_close_tick_drop_count)} />
            </div>
          </div>

          <div className="panel p-4">
            <SectionLabel icon={AlertTriangle} title="Recent Runtime Events" extra={`${recentEvents.length} recent`} />
            <DataTable columns={eventColumns} data={recentEvents} loading={monitoringLoading} emptyText="No recent runtime events" />
          </div>
        </>
      )}

      {/* Manual controls */}
      <div>
        <div className="text-2xs font-mono text-txt-label uppercase tracking-widest mb-3">Manual Controls</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {CONTROLS.map((ctrl) => {
            const mut = mutations[ctrl.key];
            const Icon = ctrl.icon;
            return (
              <div key={ctrl.key} className="card p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded bg-amber/10 border border-amber/20 shrink-0">
                    <Icon size={13} className="text-amber" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-txt-primary">{ctrl.title}</div>
                    <div className="text-2xs font-mono text-info/70 mt-0.5">{ctrl.endpoint}</div>
                    <div className="text-xs text-txt-muted mt-1 leading-relaxed">{ctrl.desc}</div>
                  </div>
                </div>
                <button
                  onClick={() => setConfirm({ key: ctrl.key, title: ctrl.title, message: ctrl.confirm })}
                  disabled={mut.isPending}
                  className="btn-outline w-full flex items-center justify-center gap-2 text-xs"
                >
                  {mut.isPending ? <Spinner size={12} /> : <Icon size={12} />}
                  {mut.isPending ? 'Running…' : `Trigger ${ctrl.title}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}

      {tab === 'logs' && <AdminRuntimeLogsTab />}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel="Yes, proceed"
        onConfirm={() => { mutations[confirm.key].mutate(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
